/**
 * Plan Schedule Validation Schemas
 *
 * The schedule write is a map of dates to workout hashids, and every rule the
 * API enforces on it is one a client can check before spending a request:
 * the keys must be `YYYY-MM-DD`, none of them may be behind today, and each
 * value must name a workout. Catching those here turns a 422 into an inline
 * message, and — more importantly — stops a schedule the user did not intend
 * from being sent at all, since the write replaces their whole future.
 *
 * Ownership is deliberately *not* checked here. Whether a workout belongs to
 * this plan is the server's to answer, and a client-side copy of that rule
 * would go stale the moment a workout is removed from the plan elsewhere.
 */

import { z } from './zod';
import { dayjs } from '../utils/date';

/** `YYYY-MM-DD`, the only key format the API accepts. */
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Today at the start of day, in the caller's zone.
 *
 * The server compares against its own `startOfDay`, so this can only ever be
 * an approximation of the boundary — which is why the page takes `today` from
 * the payload for anything it renders. Here it is a guard against the obvious
 * mistake, not the authority.
 */
const startOfToday = (): dayjs.Dayjs => dayjs().startOf('day');

/** A single scheduled date. */
export const scheduleDateKeySchema = z
  .string()
  .regex(DATE_KEY, 'Schedule dates must be formatted as YYYY-MM-DD')
  .refine((date) => dayjs(date, 'YYYY-MM-DD', true).isValid(), {
    message: 'That is not a real date',
  })
  .refine((date) => !dayjs(date).isBefore(startOfToday(), 'day'), {
    message:
      'A session cannot be scheduled in the past. Add a make-up on a future date instead.',
  });

/** The workout a date points at. */
export const scheduleWorkoutIdSchema = z
  .string()
  .trim()
  .min(1, 'A workout is required for every scheduled day');

/**
 * The schedule map itself.
 *
 * A record, not an array of pairs, so one date can only ever hold one workout —
 * the shape enforces the contract's central rule rather than restating it as a
 * check. An empty map is valid and meaningful: it is the stand-down write.
 */
export const planScheduleMapSchema = z.record(
  scheduleDateKeySchema,
  scheduleWorkoutIdSchema
);

/** `PATCH /api/plan/{plan}/schedule` body. */
export const planScheduleInputSchema = z.object({
  schedule: planScheduleMapSchema,
});

export type PlanScheduleMapInput = z.input<typeof planScheduleMapSchema>;
export type PlanScheduleMapOutput = z.output<typeof planScheduleMapSchema>;
export type PlanScheduleInput = z.input<typeof planScheduleInputSchema>;
export type PlanScheduleOutput = z.output<typeof planScheduleInputSchema>;

/**
 * Validate a schedule write. Throws a {@link z.ZodError} on failure; service
 * callers wrap it via `invalidDataErrorFromZod` for a user-facing message.
 *
 * @param data - The schedule payload to validate
 * @returns The parsed payload
 */
export function validatePlanSchedule(data: unknown): PlanScheduleOutput {
  return planScheduleInputSchema.parse(data);
}
